// ═══════════════════════════════════════════════════════════════
// RiskPortal1 — workflow-stage visibility helpers (single source)
// ---------------------------------------------------------------
// A risk moves through the stages the admin configured in the Workflow screen
// (fully customisable: any number, any order, any owner_role). At any moment it
// sits on its CURRENT stage, whose `owner_role` is the role responsible for
// acting next. These helpers answer two questions from that config:
//   1. Which role owns a given risk's current stage?  → stageOwnerRoleFor()
//   2. Should the signed-in user see this risk?        → visibleToMe()
//
// Nothing here is hardcoded to a fixed 7-stage flow — everything derives from
// the passed `stages` list (falling back to DEFAULT_BLUEPRINT only when the org
// has saved no config at all).
//
// Security note: this is UX scoping only. The backend MUST independently
// enforce visibility — hiding a row never secures an endpoint.
// ═══════════════════════════════════════════════════════════════

import { DEFAULT_BLUEPRINT } from './Pages/RiskDetail/constants';

// The signed-in user's email, lower-cased. Mirrors the reference portal's
// resolution order: dashboard profile first, then the cached portal user.
export const currentUserEmail = () => {
    try {
        const up = JSON.parse(window.localStorage.getItem('user_profile') || '{}');
        const pu = JSON.parse(window.localStorage.getItem('risk_portal_user') || '{}');
        return String(up.email || up.emailId || pu.email || '').toLowerCase();
    } catch {
        return '';
    }
};

// The full stage object a risk is currently sitting on. Prefers the
// backend-supplied `current_stage`; otherwise maps the legacy `state` to a
// stage (same fallback used by RiskDetail's flow resolver). Returns the first
// stage as a last resort so callers always get a stage to render.
export const resolveCurrentStage = (risk, stages) => {
    const list = (Array.isArray(stages) && stages.length) ? stages : DEFAULT_BLUEPRINT;
    const byId = Object.fromEntries(list.map((s) => [s.id, s]));
    const ids = list.map((s) => s.id);
    let cur = risk?.current_stage;
    if (!cur || !(cur in byId)) {
        const st = risk?.state;
        const fb = st === 'closed' ? 'closed'
            : st === 'accepted' ? 'monitoring'
                : st === 'in_progress' ? 'treatment'
                    : ids[0];
        cur = (fb in byId) ? fb : ids[0];
    }
    return byId[cur] || null;
};

// owner_role of the stage a risk is currently sitting on. Returns null when
// the stage has no owner (e.g. a terminal "Closed" stage).
export const stageOwnerRoleFor = (risk, stages) => {
    if (!risk) return null;
    return resolveCurrentStage(risk, stages)?.owner_role || null;
};

// Is it the signed-in user's turn to act on this risk? True only when their
// role owns the risk's CURRENT stage — mirrors RiskDetail's `canAct`. The
// `risk_owner` stage is scoped to the specific named owner (email), not to
// everyone holding the risk_owner role.
export const canActOnRisk = (risk, { role, email, stages } = {}) => {
    const owner = stageOwnerRoleFor(risk, stages);
    if (!owner || owner !== role) return false;
    if (owner === 'risk_owner') {
        return Boolean(email) && String(risk?.risk_owner || '').toLowerCase() === email;
    }
    return true;
};

// Roles that oversee the whole register and always see every risk. Derived
// from CAPABILITIES (not role names) so admin-created custom roles work too:
//   • can_configure → admin (configures the workflow)
//   • can_assess    → manager (assesses across the register)
// Plus the read-only executive role, which oversees but holds no capability.
// Everyone else (owners, reviewers, approvers, custom roles) is stage-scoped.
export const isOversightRole = (role, can) => {
    if (typeof can === 'function' && (can('can_configure') || can('can_assess'))) return true;
    return role === 'risk_admin' || role === 'risk_manager' || role === 'ciso';
};

// "Both combined" visibility: a scoped user sees a risk when they are the named
// owner OR their role owns the risk's current stage. Oversight roles see all.
export const visibleToMe = (risk, { role, email, stages } = {}) => {
    if (!risk) return false;
    // The named owner always sees their own risk, through every stage.
    if (email && String(risk.risk_owner || '').toLowerCase() === email) return true;
    const owner = stageOwnerRoleFor(risk, stages);
    if (!role || owner !== role) return false;
    // The `risk_owner` stage is assigned to a SPECIFIC person (the named owner,
    // matched above) — not to everyone holding the risk_owner role. So a bare
    // role match on that stage does NOT grant visibility; only being the named
    // owner does. Every other stage is genuinely role-based.
    if (owner === 'risk_owner') return false;
    return true;
};
